/**
 * @file SetHomePageDialog.tsx
 * @description «Сделать главной» (phase19.16 Р2; uiux-design-r2 §6; SB29;
 * Р3а — §1.3 db-design-r3a, решение пользователя 2026-10-23): выбранная
 * страница становится главной, прежняя главная — обычной страницей с новым
 * адресом (его вписывает человек) и **остаётся видимой**; скрыть её —
 * галочкой (по умолчанию снята). Главной становится версия на сайте; с
 * галочкой «вместе с правками» — черновик (нужен отпечаток, 409 «черновик
 * изменился»). Страница, которой ещё не было на сайте, — только с черновиком.
 * Скрытая страница — [Показать и сделать главной] (`publish: true`).
 * С Р3в (SB131) с прежнего адреса новой главной сервер ставит переадресацию на «/» — так и пишем.
 */

import { useEffect, useState } from "react";

import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { useNotify } from "../../ui/NotifyProvider";
import { CheckRow } from "../../site-view/CheckRow";
import { TextField } from "../../site-view/TextField";
import { ApiError } from "../../../lib/api-client";
import { sitePagePublish, type SetHomeResult, type SitePageChangesDto } from "../../../lib/api-site-page-publish";
import { blockErrorText } from "../../../lib/site-builder/block-errors";
import { cleanSlugInput, PAGE_SLUG_MAX, slugFromTitle } from "../../../lib/site-builder/page-form";
import { draftEditCount, editsWord } from "../../../lib/site-builder/page-status";
import { draftChangedText, parseDraftChanged } from "../../../lib/site-builder/page-publish-errors";
import { notifyRedirectChange } from "../../../lib/site-seo/redirect-change";

/** Ошибки сервера, которые относятся к полю адреса. */
const SLUG_ERRORS = new Set(["site_page_previous_slug_required", "site_page_slug_reserved", "site_page_slug_taken", "validation_failed"]);

interface PageRef {
  id: string;
  title: string;
  isPublished: boolean;
}

interface Props {
  open: boolean;
  page: PageRef | null;
  /** Текущая главная; `null` — главной нет. */
  home: { title: string } | null;
  /** Право публикации контента (SB29: «Сделать главной» — публикация). */
  canPublish: boolean;
  onClose: () => void;
  onDone: (result: SetHomeResult) => void | Promise<void>;
}

export function SetHomePageDialog({ open, page, home, canPublish, onClose, onDone }: Props) {
  const notify = useNotify();
  const [slug, setSlug] = useState("");
  const [hidePrevious, setHidePrevious] = useState(false);
  const [withDraft, setWithDraft] = useState(false);
  const [draft, setDraft] = useState<SitePageChangesDto | null>(null);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pageId = page?.id ?? null;

  async function loadDraft(id: string) {
    try {
      setDraft(await sitePagePublish.changes(id));
    } catch {
      setDraft(null);
    }
  }

  useEffect(() => {
    if (!open) return;
    setSlug(home ? slugFromTitle(`${home.title} прежняя`).slice(0, PAGE_SLUG_MAX) : "");
    setHidePrevious(false);
    setWithDraft(false);
    setDraft(null);
    setSlugError(null);
    setError(null);
    if (pageId) void loadDraft(pageId);
  }, [open, home, pageId]);

  if (!page) return null;
  const hidden = !page.isPublished;
  const neverLive = draft !== null && draft.live === null;
  const edits = draft?.hasChanges && draft.live ? Math.max(1, draftEditCount(draft)) : 0;
  const useDraft = neverLive || (withDraft && edits > 0);

  async function submit() {
    if (!page) return;
    if (home && !slug.trim()) {
      setSlugError("Укажите новый адрес прежней главной.");
      return;
    }
    setBusy(true);
    setError(null);
    setSlugError(null);
    try {
      const result = await sitePagePublish.setHome(page.id, {
        ...(home ? { previousSlug: slug, hidePrevious } : {}),
        ...(hidden ? { publish: true } : {}),
        ...(useDraft && draft ? { withDraft: true, fingerprint: draft.fingerprint } : {}),
      });
      await onDone(result);
      // Р3в (SB29, SB131): прежний адрес новой главной → «/» — тостом.
      notifyRedirectChange(result.redirect, notify);
      onClose();
    } catch (err) {
      const changed = parseDraftChanged(err);
      if (changed) {
        setError(draftChangedText(changed, page.title));
        void loadDraft(page.id);
      } else {
        const text = blockErrorText(err, "Не удалось сделать страницу главной.");
        if (err instanceof ApiError && SLUG_ERRORS.has(err.code)) setSlugError(text);
        else setError(text);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent fullScreen className="gap-4">
        <DialogHeader>
          <DialogTitle>Сделать «{page.title}» главной?</DialogTitle>
          <DialogDescription>
            Покупатели будут попадать на неё по адресу сайта. Меню не сломается; с прежнего адреса страницы будет переадресация на главную.
          </DialogDescription>
        </DialogHeader>
        {hidden ? (
          <p className="type-body rounded-lg bg-warning/10 px-3 py-2 text-on-surface">
            Страница скрыта — она станет главной и сразу появится на сайте.
            {canPublish ? "" : " Сделать это может сотрудник с правом публикации контента."}
          </p>
        ) : null}
        {neverLive ? (
          <p className="type-body text-on-surface-variant">Страница ещё не была на сайте — главной станет её черновик.</p>
        ) : edits > 0 ? (
          <CheckRow checked={withDraft} onChange={setWithDraft}>
            Опубликовать вместе с неопубликованными правками ({editsWord(edits)})
          </CheckRow>
        ) : null}
        {home ? (
          <>
            <TextField
              label={`Новый адрес страницы «${home.title}»`}
              value={slug}
              max={PAGE_SLUG_MAX}
              error={slugError}
              hint="Прежняя главная останется обычной страницей на сайте. Латиница, цифры и дефис."
              onChange={(v) => setSlug(cleanSlugInput(v))}
            />
            <CheckRow checked={hidePrevious} onChange={setHidePrevious}>
              Скрыть прежнюю главную с сайта
            </CheckRow>
          </>
        ) : null}
        {error ? <p role="alert" className="type-caption text-destructive">{error}</p> : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button loading={busy} disabled={!canPublish || (useDraft && !draft)} onClick={() => void submit()}>
            {hidden ? "Показать и сделать главной" : "Сделать главной"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
